import { API_ENDPOINTS } from '../constants';
import { User, UserRole } from '../types';

import { get, post, put, del } from './apiClient';

// User creation/update interfaces
export interface UserCreate {
  username: string;
  email: string;
  full_name: string;
  password: string;
  role: UserRole;
  district_id?: number;
  school_id?: number;
}

export interface UserUpdate {
  username?: string;
  email?: string;
  full_name?: string;
  password?: string;
  role?: UserRole;
  is_active?: boolean;
  district_id?: number;
  school_id?: number;
}

// Users list parameters - aligned with backend auth/users endpoint
export interface UsersListParams {
  skip?: number;
  limit?: number;
  search?: string;
  school_id?: number;
  district_id?: number;
  role?: UserRole;
  is_active?: boolean;
}

export interface UsersResponse {
  data: User[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
    next_page: number | null;
    prev_page: number | null;
  };
}

// Get users list
export const getUsers = async (
  params: UsersListParams = {}
): Promise<UsersResponse> => {
  try {
    const queryParams = new URLSearchParams();

    if (params.skip !== undefined)
      queryParams.append('skip', params.skip.toString());
    if (params.limit !== undefined)
      queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.school_id)
      queryParams.append('school_id', params.school_id.toString());
    if (params.district_id)
      queryParams.append('district_id', params.district_id.toString());
    if (params.role) queryParams.append('role', params.role);
    if (params.is_active !== undefined)
      queryParams.append('is_active', params.is_active.toString());

    const queryString = queryParams.toString();
    const endpoint = `${API_ENDPOINTS.USERS.LIST}${queryString ? `?${queryString}` : ''}`;

    return await get<UsersResponse>(endpoint);
  } catch (error) {
    console.error('Get users error:', error);
    throw error;
  }
};

// Get user by ID
export const getUser = async (userId: number): Promise<User> => {
  try {
    return await get<User>(API_ENDPOINTS.USERS.GET(userId.toString()));
  } catch (error) {
    console.error('Get user error:', error);
    throw error;
  }
};

// Create user
export const createUser = async (userData: UserCreate): Promise<User> => {
  try {
    return (await post(API_ENDPOINTS.USERS.CREATE, userData)) as User;
  } catch (error) {
    console.error('Create user error:', error);
    throw error;
  }
};

// Update user
export const updateUser = async (
  userId: number,
  userData: UserUpdate
): Promise<User> => {
  try {
    return (await put(
      API_ENDPOINTS.USERS.UPDATE(userId.toString()),
      userData
    )) as User;
  } catch (error) {
    console.error('Update user error:', error);
    throw error;
  }
};

// Delete user
export const deleteUser = async (userId: number): Promise<void> => {
  try {
    await del(API_ENDPOINTS.USERS.DELETE(userId.toString()));
  } catch (error) {
    console.error('Delete user error:', error);
    throw error;
  }
};

export const setUserActive = async (
  userId: number,
  isActive: boolean
): Promise<void> => {
  await post(`/users/${userId}/${isActive ? 'activate' : 'deactivate'}`);
};

// Export service object
export const usersService = {
  getUsers,
  getUser,
  createUser,
  updateUser,
  deleteUser,
  setUserActive,
};
